import React, { useMemo, useState, useRef, useEffect } from "react";
import styles from "./TabManagement.module.css";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import { Tab } from "../../types";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

interface FormState {
  id?: string;
  nameRu: string;
  nameEn?: string;
  sortOrder?: number;
}

export default function TabManagement() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const modalContentRef = useRef<HTMLDivElement>(null);

  const { data: tabs, isLoading } = useQuery({
    queryKey: ["tabs"],
    queryFn: () => apiService.getTabs(),
  });

  const editableTabs = useMemo(
    () =>
      (tabs || [])
        .filter((t) => t.isEditable)
        .sort((a, b) => {
          const orderA = (a as any).sortOrder ?? 0;
          const orderB = (b as any).sortOrder ?? 0;
          return orderA - orderB;
        }),
    [tabs]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const updateOrderMutation = useMutation({
    mutationFn: async (tabs: { id: string; sortOrder: number }[]) => {
      return apiService.updateTabsOrder(tabs);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tabs"] });
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = editableTabs.findIndex((t) => t.id === active.id);
      const newIndex = editableTabs.findIndex((t) => t.id === over.id);

      const newTabs = arrayMove(editableTabs, oldIndex, newIndex);
      const tabsWithOrder = newTabs.map((tab, index) => ({
        id: tab.id,
        sortOrder: index + 1,
      }));

      updateOrderMutation.mutate(tabsWithOrder);
    }
  };

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formState, setFormState] = useState<FormState>({
    nameRu: "",
  });
  const [formError, setFormError] = useState<string>("");

  const resetForm = () => {
    setFormState({ nameRu: "" });
    setFormError("");
  };

  const openCreate = () => {
    resetForm();
    setFormError("");
    createMutation.reset();
    updateMutation.reset();
    setIsFormOpen(true);
  };

  const openEdit = (tab: Tab) => {
    setFormState({
      id: tab.id,
      nameRu: tab.name.ru || "",
      nameEn: tab.name.en || "",
    });
    setFormError("");
    createMutation.reset();
    updateMutation.reset();
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    resetForm();
  };

  const createMutation = useMutation({
    mutationFn: async (payload: FormState) => {
      // Calculate max sortOrder and add 1 for new tab
      const maxSortOrder =
        editableTabs.length > 0
          ? Math.max(...editableTabs.map((t) => (t as any).sortOrder ?? 0))
          : 0;

      const body: any = {
        name: { ru: payload.nameRu, en: payload.nameEn || undefined },
        isEditable: true,
        sortOrder:
          payload.sortOrder !== undefined
            ? Number(payload.sortOrder)
            : maxSortOrder + 1,
      };
      return apiService.createTab(body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tabs"] });
      closeForm();
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message || "Ошибка при создании вкладки";
      setFormError(errorMessage);
      setTimeout(() => setFormError(""), 5000);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: FormState) => {
      if (!payload.id) throw new Error("Missing id");
      const body: any = {
        name: { ru: payload.nameRu, en: payload.nameEn || undefined },
      };
      return apiService.updateTab(payload.id, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tabs"] });
      closeForm();
    },
    onError: (error: any) => {
      const errorMessage =
        error.response?.data?.message || "Ошибка при обновлении вкладки";
      setFormError(errorMessage);
      setTimeout(() => setFormError(""), 5000);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => apiService.deleteTab(id),
    onSuccess: (_, deletedTabId) => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: ["tabs"] });
      queryClient.invalidateQueries({ queryKey: ["shops"] });
      // Invalidate categories for all possible tab IDs (since we don't know which ones were deleted)
      queryClient.invalidateQueries({ queryKey: ["categories"] });

      // Also trigger a custom event to update AppContext
      window.dispatchEvent(new CustomEvent("tabsUpdated"));
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const validationMessages: string[] = [];

    // Локальная проверка обязательных полей, как в модалке "Добавить объект"
    if (!formState.nameRu || !formState.nameRu.trim()) {
      validationMessages.push(
        "• Название (RU): обязательно для заполнения и не может быть пустым"
      );
    }

    if (validationMessages.length > 0) {
      setFormError(`Ошибки валидации:\n${validationMessages.join("\n")}`);
      return;
    }

    setFormError("");
    if (formState.id) {
      updateMutation.mutate(formState);
    } else {
      createMutation.mutate(formState);
    }
  };

  const handleDelete = (tab: Tab) => {
    if (
      window.confirm(
        "Удаление вкладки приведет к удалению связанных Объектов и Категорий. Удалить?"
      )
    ) {
      deleteMutation.mutate(tab.id);
    }
  };

  useEffect(() => {
    if (isFormOpen && modalContentRef.current) {
      modalContentRef.current.scrollTop = 0;
    }
  }, [isFormOpen]);

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.logo}>Управление вкладками</div>
        <button
          className={styles.backButton}
          onClick={() => navigate("/admin")}
        >
          Назад
        </button>
      </header>

      <main className={styles.mainContent}>
        <h1 className={styles.pageTitle}>Вкладки</h1>

        <button className={styles.addButton} onClick={openCreate}>
          + Добавить вкладку
        </button>

        {isLoading ? (
          <div className={styles.loadingSpinner}>Загрузка...</div>
        ) : editableTabs && editableTabs.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <div className={styles.table}>
              <div className={styles.tableHeader}>
                <div className={styles.tableHeaderCell}></div>
                <div className={styles.tableHeaderCell}>Название (RU)</div>
                <div className={styles.tableHeaderCell}>Название (EN)</div>
                <div className={styles.tableHeaderCell}>Действия</div>
              </div>
              <SortableContext
                items={editableTabs.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
              >
                <div className={styles.tableBody}>
                  {editableTabs.map((t) => (
                    <SortableTabItem
                      key={t.id}
                      tab={t}
                      onEdit={() => openEdit(t)}
                      onDelete={() => handleDelete(t)}
                    />
                  ))}
                </div>
              </SortableContext>
            </div>
          </DndContext>
        ) : (
          <div className={styles.emptyState}>Вкладки отсутствуют</div>
        )}

        <div
          className={`${styles.modal} ${isFormOpen ? styles.modalOpen : ""}`}
        >
          <div
            ref={modalContentRef}
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className={styles.modalTitle}>
              {formState.id ? "Редактировать вкладку" : "Создать вкладку"}
            </h2>
            {formError && (
              <div
                className={styles.errorMessage}
                style={{ whiteSpace: "pre-line" }}
              >
                {formError}
              </div>
            )}
            <form onSubmit={onSubmit}>
              <div className={styles.formGroup}>
                <label className={styles.label}>Название (RU)*</label>
                <input
                  className={styles.input}
                  value={formState.nameRu}
                  onChange={(e) => {
                    setFormState((s) => ({ ...s, nameRu: e.target.value }));
                    if (formError) {
                      setFormError("");
                    }
                  }}
                />
              </div>
              <div className={styles.formGroup}>
                <label className={styles.label}>Название (EN)</label>
                <input
                  className={styles.input}
                  value={formState.nameEn || ""}
                  onChange={(e) =>
                    setFormState((s) => ({ ...s, nameEn: e.target.value }))
                  }
                />
              </div>
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={`${styles.actionButton} ${styles.actionWarning}`}
                  onClick={closeForm}
                >
                  Отмена
                </button>
                <button
                  type="submit"
                  className={styles.actionButton}
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                >
                  {formState.id ? "Сохранить" : "Создать"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
}

interface SortableTabItemProps {
  tab: Tab;
  onEdit: () => void;
  onDelete: () => void;
}

function SortableTabItem({ tab, onEdit, onDelete }: SortableTabItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`${styles.tableRow} ${isDragging ? styles.dragging : ""}`}
    >
      <div className={styles.dragHandle} {...attributes} {...listeners}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="7" cy="5" r="1.5" fill="currentColor" />
          <circle cx="13" cy="5" r="1.5" fill="currentColor" />
          <circle cx="7" cy="10" r="1.5" fill="currentColor" />
          <circle cx="13" cy="10" r="1.5" fill="currentColor" />
          <circle cx="7" cy="15" r="1.5" fill="currentColor" />
          <circle cx="13" cy="15" r="1.5" fill="currentColor" />
        </svg>
      </div>
      <div className={styles.tableCell}>{tab.name.ru}</div>
      <div className={styles.tableCell}>{tab.name.en || "—"}</div>
      <div className={`${styles.tableCell} ${styles.actionCell}`}>
        <button className={styles.actionButton} onClick={onEdit}>
          Редактировать
        </button>
        <button
          className={`${styles.actionButton} ${styles.actionDanger}`}
          onClick={onDelete}
        >
          Удалить
        </button>
      </div>
    </div>
  );
}
